// TanStack Query hooks for the Tarjeta Presente project brain.
// Reads go straight to Supabase (RLS org-scoped). Writes/ask/sync go through
// the edge fns via invokeEdgeOrThrow (embeddings + graph wiring are server-side).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { invokeEdgeOrThrow } from '@/lib/edge-functions'
import { useOrg } from '@/contexts/OrgContext'
import {
  BRAIN_PROJECT_TP,
  type BrainAskResponse,
  type BrainPage,
  type BrainPageMeta,
  type BrainPageType,
  type BrainSearchHit,
  type BrainSyncResponse,
  type BrainTeam,
  type BrainWriteResponse,
} from '@/types/brain'

const KEY = 'brain'

export function useBrainPages(opts: { pageType?: BrainPageType; team?: BrainTeam; limit?: number } = {}) {
  const { orgId } = useOrg()
  return useQuery({
    queryKey: [KEY, 'pages', orgId, opts.pageType ?? 'all', opts.team ?? 'all', opts.limit ?? 200],
    enabled: !!orgId,
    queryFn: async (): Promise<BrainPageMeta[]> => {
      let q = supabase
        .from('brain_pages')
        .select('id, org_id, project, slug, page_type, title, frontmatter, source, source_ref, created_by, created_at, updated_at')
        .eq('org_id', orgId!)
        .eq('project', BRAIN_PROJECT_TP)
        .order('updated_at', { ascending: false })
        .limit(opts.limit ?? 200)
      if (opts.pageType) q = q.eq('page_type', opts.pageType)
      // Sección por equipo (frontmatter.team, mig 202) — filtro server-side:
      // filtrar en cliente sobre la ventana de 200 daría vacíos falsos.
      if (opts.team) q = q.contains('frontmatter', { team: opts.team })
      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as BrainPageMeta[]
    },
  })
}

export function useBrainPage(slug: string | null) {
  const { orgId } = useOrg()
  return useQuery({
    queryKey: [KEY, 'page', orgId, slug],
    enabled: !!orgId && !!slug,
    queryFn: async (): Promise<BrainPage | null> => {
      const { data, error } = await supabase
        .from('brain_pages')
        // Columnas explícitas: '*' bajaría el embedding (~30KB) y el tsvector
        // al browser en cada apertura de página.
        .select('id, org_id, project, slug, page_type, title, content_md, frontmatter, source, source_ref, created_by, created_at, updated_at')
        .eq('org_id', orgId!)
        .eq('project', BRAIN_PROJECT_TP)
        .eq('slug', slug!)
        .maybeSingle()
      if (error) throw error
      return (data as BrainPage) ?? null
    },
  })
}

export interface BrainBacklink {
  link_type: string
  page: Pick<BrainPageMeta, 'id' | 'slug' | 'title' | 'page_type' | 'updated_at'>
}

export function useBrainBacklinks(pageId: string | null) {
  const { orgId } = useOrg()
  return useQuery({
    queryKey: [KEY, 'backlinks', orgId, pageId],
    enabled: !!orgId && !!pageId,
    queryFn: async (): Promise<BrainBacklink[]> => {
      const { data: links, error } = await supabase
        .from('brain_links')
        .select('link_type, from_page')
        .eq('org_id', orgId!)
        .eq('to_page', pageId!)
        .limit(50)
      if (error) throw error
      if (!links || links.length === 0) return []
      const { data: pages, error: pErr } = await supabase
        .from('brain_pages')
        .select('id, slug, title, page_type, updated_at')
        .eq('org_id', orgId!)
        .eq('project', BRAIN_PROJECT_TP)
        .in('id', links.map((l) => l.from_page))
      if (pErr) throw pErr
      const byId = new Map((pages ?? []).map((p) => [p.id, p]))
      return links
        .map((l) => ({ link_type: l.link_type, page: byId.get(l.from_page) }))
        .filter((b): b is BrainBacklink => !!b.page)
    },
  })
}

// HYBRID search (vector + FTS) via brain-ask's search_only mode — the query
// embedding must be generated server-side. Falls back to the FTS-only client
// RPC if the edge fn is unreachable, so search never goes fully dark.
export function useBrainSearch(term: string) {
  const { orgId } = useOrg()
  const trimmed = term.trim()
  return useQuery({
    queryKey: [KEY, 'search', orgId, trimmed],
    enabled: !!orgId && trimmed.length >= 3,
    queryFn: async (): Promise<BrainSearchHit[]> => {
      try {
        const res = await invokeEdgeOrThrow<{ success: boolean; hits: BrainSearchHit[] }>(
          'brain-ask',
          { org_id: orgId, project: BRAIN_PROJECT_TP, question: trimmed, search_only: true },
          { timeoutMs: 20000 },
        )
        return res.hits ?? []
      } catch {
        const { data, error } = await supabase.rpc('brain_hybrid_search', {
          p_org_id: orgId!,
          p_project: BRAIN_PROJECT_TP,
          p_query: trimmed,
          p_embedding: null,
          p_page_type: null,
          p_limit: 15,
        })
        if (error) throw error
        return (data ?? []) as BrainSearchHit[]
      }
    },
  })
}

export interface BrainWriteInput {
  slug?: string
  page_type: BrainPageType
  title: string
  content_md: string
  frontmatter?: Record<string, unknown>
  source?: string
  source_ref?: string
}

export function useBrainWrite() {
  const { orgId } = useOrg()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BrainWriteInput) =>
      invokeEdgeOrThrow<BrainWriteResponse>(
        'brain-write',
        { ...input, org_id: orgId, project: BRAIN_PROJECT_TP },
        { timeoutMs: 30000 },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useBrainDelete() {
  const { orgId } = useOrg()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (pageId: string) => {
      // RLS silently filters rows the user may not delete (creator-or-admin
      // policy) and PostgREST returns success with 0 rows — verify honestly.
      const { data, error } = await supabase
        .from('brain_pages')
        .delete()
        .eq('id', pageId)
        .eq('org_id', orgId!)
        .select('id')
      if (error) throw error
      if (!data || data.length === 0) {
        throw new Error('No tienes permiso para borrar esta página (solo su creador o un admin)')
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useBrainAsk() {
  const { orgId } = useOrg()
  return useMutation({
    mutationFn: (question: string) =>
      invokeEdgeOrThrow<BrainAskResponse>(
        'brain-ask',
        { org_id: orgId, project: BRAIN_PROJECT_TP, question },
        { timeoutMs: 90000 },
      ),
  })
}

const SYNC_FN: Record<'gong' | 'sf' | 'fireflies', string> = {
  gong: 'tp-gong-sync',
  sf: 'tp-sf-sync',
  fireflies: 'tp-fireflies-sync',
}

export function useBrainSync(kind: 'gong' | 'sf' | 'fireflies') {
  const { orgId } = useOrg()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      invokeEdgeOrThrow<BrainSyncResponse>(
        SYNC_FN[kind],
        { org_id: orgId, project: BRAIN_PROJECT_TP },
        { timeoutMs: 180000 },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}
